/**
 * The purchaser's side of the Rule 502(b) package (S-30): the consent to electronic delivery, the
 * documents of the offering, their acknowledgement, questions to the issuer and the
 * sophistication questionnaire of a purchaser who is not accredited.
 *
 * Everything is keyed by the contract case being signed; the server decides what the person may
 * see and answers the same 404 for a case that is not theirs.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type RegDDocType =
  | 'reg_d_package'
  | 'exhibit'
  | 'bad_actor_disclosure'
  | 'ppm'
  | 'term_sheet'
  | 'supplement';

export interface RegDDocument {
  id: string;
  doc_type: RegDDocType;
  exhibit_slot: string | null;
  version: number;
  file_hash: string;
  scope: 'issuer' | 'product';
  published_at: string;
  delivered: boolean;
  acknowledged: boolean;
}

export interface RegDQuestion {
  question_id: string;
  question: string;
  asked_at: string;
  answer: string | null;
  answered_at: string | null;
}

export interface RegDStatus {
  legal_entity_id: string;
  consent: { given: boolean; version: string; text: string };
  documents: RegDDocument[];
  acknowledgement: { version: string; text: string; acknowledged_at: string | null };
  min_hours: number;
  non_accredited_missing: string[];
  accredited_missing: string[];
  earliest_signing_at: string | null;
  sophistication: { state: SophisticationState; reason: string | null };
  questions: RegDQuestion[];
}

export type SophisticationState =
  | 'not_submitted'
  | 'awaiting_review'
  | 'qualified'
  | 'not_qualified';

export interface QuestionnaireItem {
  key: string;
  text: string;
  choices: string[];
  required: boolean;
}

export interface Questionnaire {
  version: string;
  items: QuestionnaireItem[];
}

class RegDService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async getStatus(caseId: string): Promise<RegDStatus> {
    const res = await this.api.get<RegDStatus>(`/api/v1/reg-d/cases/${caseId}/status`);
    return res.data;
  }

  /** The sample document whose code the consent asks for, as a blob the browser can open. */
  async getConsentSample(): Promise<Blob> {
    const res = await this.api.get<Blob>('/api/v1/reg-d/consent/sample', {
      responseType: 'blob',
    });
    return res.data;
  }

  async giveConsent(code: string): Promise<void> {
    await this.api.post('/api/v1/reg-d/consent', { code });
  }

  async withdrawConsent(): Promise<void> {
    await this.api.post('/api/v1/reg-d/consent/withdraw');
  }

  async getDocumentUrl(caseId: string, documentId: string): Promise<string> {
    const res = await this.api.get<{ download_url: string }>(
      `/api/v1/reg-d/cases/${caseId}/documents/${documentId}/download`,
    );
    return res.data.download_url;
  }

  async acknowledge(caseId: string): Promise<void> {
    await this.api.post(`/api/v1/reg-d/cases/${caseId}/acknowledge`);
  }

  async askQuestion(caseId: string, question: string): Promise<void> {
    await this.api.post(`/api/v1/reg-d/cases/${caseId}/questions`, { question });
  }

  async getQuestionnaire(caseId: string): Promise<Questionnaire> {
    const res = await this.api.get<{ questionnaire: Questionnaire }>(
      `/api/v1/reg-d/cases/${caseId}/sophistication`,
    );
    return res.data.questionnaire;
  }

  async submitAnswers(caseId: string, answers: Record<string, string>): Promise<void> {
    await this.api.post(`/api/v1/reg-d/cases/${caseId}/sophistication`, { answers });
  }
}

export default new RegDService();
